import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { RELATION_PART_OF } from '@backstage/catalog-model';
import { EntityWarningContent } from '@internal/backstage-plugin-devops-react';
import {
  CloudResourceEntity,
  InsightEntity,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import { useRulesByEntity } from '@internal/backstage-plugin-devops-react';
import { RegionChip } from '../RegionChip';
import { findingPillValue, pillClass } from './shared';
import styles from './entity-page.module.css';

/** Static explainer per finding type; hidden for unknown types. */
const EXPLAINERS: Record<string, string> = {
  external_access_analyzer:
    'IAM Access Analyzer identified a principal outside your organization with access to this resource through its policy. Findings of this type should be triaged: confirm the external access is intended, otherwise restrict the policy. The finding resolves automatically once the analyzer re-scans and the exposure is gone.',
};

const isGlobal = (insight: InsightEntity): boolean =>
  (insight.spec.region ?? '').toLowerCase() === 'global';

/**
 * Insight page Overview, 1:1 with design/insight-page-mockup.html: the
 * drawer's expanded twin — type explainer, finding meta grid, resource card
 * with the compliance state of that resource, and related findings of the
 * same type on the account. "Exposed through" renders only when the
 * analyzer payload carries the policy detail (not in spec today).
 */
export function InsightOverview() {
  const { entity } = useEntity();
  const insight = entity as InsightEntity;
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const accountRelations = getEntityRelations(insight, RELATION_PART_OF, {
    kind: 'account',
  });
  const accountName = accountRelations[0]?.name ?? '';

  // The finding's resource entity, matched by name or title.
  const [resourceEntity, setResourceEntity] = useState<
    CloudResourceEntity | undefined
  >(undefined);
  // Related findings: same account, same type.
  const [related, setRelated] = useState<InsightEntity[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!accountName) {
      return () => {
        cancelled = true;
      };
    }
    catalogApi
      .getEntities({
        filter: { kind: 'CloudResource', 'spec.account': accountName },
      })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        const candidates = items as CloudResourceEntity[];
        setResourceEntity(
          candidates.find(
            candidate =>
              candidate.metadata.name === insight.spec.resource ||
              candidate.metadata.title === insight.spec.resource,
          ),
        );
      })
      .catch(() => {
        // The resource card simply does not render.
      });
    catalogApi
      .getEntities({ filter: { kind: 'Insight', 'spec.account': accountName } })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        setRelated(
          (items as InsightEntity[]).filter(
            candidate =>
              candidate.metadata.name !== insight.metadata.name &&
              candidate.spec.type === insight.spec.type,
          ),
        );
      })
      .catch(() => {
        // Related findings are optional.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountName, insight.metadata.name, insight.spec.resource, insight.spec.type]);

  const { rules: resourceRules } = useRulesByEntity(
    // The hook's effect reads relations only; an empty shell is safe while
    // the resource entity is still unresolved (relations [] → no request).
    resourceEntity ??
      ({
        apiVersion: 'backstage.io/v1alpha1',
        kind: 'CloudResource',
        metadata: { name: 'insight-resource-placeholder' },
      } as CloudResourceEntity),
  );
  const failedRules = resourceEntity
    ? (resourceRules as RuleEntity[]).filter(
        rule => rule.spec?.result === 'Failed',
      )
    : [];

  const explainer = EXPLAINERS[(insight.spec.type ?? '').toLowerCase()];
  const statusPillClass = findingPillValue(insight.spec.status ?? '');

  let resourceFieldValue: ReactNode = '—';
  if (insight.spec.resource) {
    resourceFieldValue = resourceEntity ? (
      <Link
        to={`/catalog/default/cloudresource/${resourceEntity.metadata.name}`}
      >
        {insight.spec.resource}
      </Link>
    ) : (
      insight.spec.resource
    );
  }

  const metaFields = [
    {
      key: 'Resource',
      value: resourceFieldValue,
    },
    { key: 'Resource type', value: insight.spec.resourceType ?? '—' },
    {
      key: 'Region',
      value: isGlobal(insight) ? (
        <span className={styles.badge}>Global</span>
      ) : (
        <RegionChip region={insight.spec.region ?? ''} />
      ),
    },
    {
      key: 'Status',
      value: (
        <span className={`${styles.pill} ${pillClass(styles, statusPillClass)}`}>
          <span className={styles.pillDot} />
          {insight.spec.status ?? 'Unknown'}
        </span>
      ),
    },
    { key: 'Updated', value: insight.spec.updatedAt ?? '—' },
    { key: 'Analyzer run', value: insight.spec.reportCreationTime ?? '—' },
    { key: 'Type', value: insight.spec.type ?? '—', mono: true },
    {
      key: 'Account',
      value: accountName ? (
        <Link to={`/catalog/default/account/${accountName}`}>{accountName}</Link>
      ) : (
        '—'
      ),
    },
  ];

  return (
    <>
      <EntityWarningContent />

      {explainer && (
        <div className={styles.infoBox}>
          <b>What this finding means.</b> {explainer}
        </div>
      )}

      <div className={styles.row2}>
        <div className={`${styles.card} ${styles.cardPad}`}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Finding</h3>
            <span className={styles.cardHint}>expanded drawer content</span>
          </div>
          <div className={styles.metaGrid}>
            {metaFields.map(field => (
              <div key={field.key}>
                <div className={styles.metaKey}>{field.key}</div>
                <div
                  className={`${styles.metaValue} ${field.mono ? styles.ruleCode : ''}`}
                  style={field.mono ? { fontSize: 12 } : undefined}
                >
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {resourceEntity && (
            <>
              <div className={`${styles.card} ${styles.cardPad}`}>
                <div className={styles.cardHead}>
                  <h3 className={styles.cardTitle}>Resource</h3>
                </div>
                <table className={styles.kvTable}>
                  <tbody>
                    <tr>
                      <td className={styles.kvKey}>Service</td>
                      <td className={styles.kvValue}>
                        {resourceEntity.spec.service ?? '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.kvKey}>Category</td>
                      <td className={styles.kvValue}>
                        {resourceEntity.spec.category ?? '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.kvKey}>Region</td>
                      <td className={styles.kvValue}>
                        {resourceEntity.spec.region ?? '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.kvKey}>Provider</td>
                      <td className={styles.kvValue}>
                        {resourceEntity.spec.provider ?? '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p style={{ marginTop: 12, fontSize: 'var(--bui-font-size-3)' }}>
                  <Link
                    to={`/catalog/default/cloudresource/${resourceEntity.metadata.name}`}
                  >
                    Open resource page →
                  </Link>
                </p>
              </div>

              <div
                className={`${styles.card} ${styles.cardPad}`}
                style={{ marginTop: 'calc(var(--bui-space) * 3)' }}
              >
                <div className={styles.cardHead}>
                  <h3 className={styles.cardTitle}>
                    Compliance on this resource
                  </h3>
                </div>
                {failedRules.length > 0 ? (
                  <p style={{ fontSize: 'var(--bui-font-size-3)' }}>
                    <span
                      className={`${styles.pill} ${styles.pillFailed}`}
                      style={{ marginRight: 6 }}
                    >
                      <span className={styles.pillDot} />
                      {failedRules.length} failed{' '}
                      {failedRules.length === 1 ? 'rule' : 'rules'}
                    </span>
                    {failedRules.slice(0, 3).map((rule, index) => (
                      <span key={rule.metadata.name}>
                        {index > 0 && ', '}
                        <Link to={`/catalog/default/rule/${rule.metadata.name}`}>
                          {rule.metadata.title ?? rule.metadata.name}
                        </Link>
                      </span>
                    ))}
                  </p>
                ) : (
                  <p className={styles.muted}>Nothing failed on this resource.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <>
          <div className={styles.secTitle}>
            Related findings on this account
          </div>
          <div className={styles.card}>
            <table className={styles.table} aria-label="Related findings">
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Resource</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {related.map(candidate => {
                  const candidatePill = findingPillValue(
                    candidate.spec.status ?? '',
                  );
                  return (
                    <tr
                      key={candidate.metadata.name}
                      className={styles.row}
                      onClick={() =>
                        navigate(
                          `/catalog/default/insight/${candidate.metadata.name}`,
                        )
                      }
                    >
                      <td>
                        <span className={styles.ruleName}>
                          {candidate.metadata.title ?? candidate.metadata.name}
                        </span>
                      </td>
                      <td className={styles.resName}>
                        {candidate.spec.resource ?? '—'}
                      </td>
                      <td>
                        {(candidate.spec.region ?? '').toLowerCase() ===
                        'global' ? (
                          <span className={styles.muted}>Global</span>
                        ) : (
                          <RegionChip region={candidate.spec.region ?? ''} />
                        )}
                      </td>
                      <td>
                        <span
                          className={`${styles.pill} ${pillClass(styles, candidatePill)}`}
                        >
                          <span className={styles.pillDot} />
                          {candidate.spec.status ?? 'Unknown'}
                        </span>
                      </td>
                      <td className={styles.muted}>
                        {candidate.spec.updatedAt ?? '—'}
                      </td>
                      <td className={styles.cellArr}>↗</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
